import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import {
  fetchLibraryModules,
  fetchLibraryModuleById,
  createLibraryModule,
  updateLibraryModule,
  deleteLibraryModule,
  publishLibraryModule,
  fetchLibraryTags,
} from "@/api/moduleLibrary";
import type {
  ModuleLibraryState,
  LibraryModule,
  LibraryModuleUsage,
  ModuleLibraryFilters,
  ModuleLibrarySort,
  ModuleLibraryFormData,
  ListLibraryModulesParams,
  CreateLibraryModuleRequest,
  UpdateLibraryModuleRequest,
  GetLibraryModuleResponse,
} from "../types/libraryModule.types";

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: ModuleLibraryFilters = {
  search: "",
  status: "",
  difficulty_level: "",
  tags: [],
  skill_ids: [],
};

const initialSort: ModuleLibrarySort = {
  field: "title",
  order: "ASC",
};

const initialState: ModuleLibraryState = {
  modules: [],
  pagination: null,
  filters: initialFilters,
  sort: initialSort,
  selectedModule: null,
  selectedModuleUsage: [],
  editorMode: null,
  formData: null,
  isDirty: false,
  availableTags: [],
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    publish: false,
    tags: false,
  },
  error: {
    list: null,
    detail: null,
    create: null,
    update: null,
    delete: null,
    publish: null,
    tags: null,
  },
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch library modules list with filters and pagination
 */
export const fetchModulesThunk = createAsyncThunk<
  { modules: LibraryModule[]; pagination: ModuleLibraryState["pagination"] },
  { page?: number } | void,
  { state: RootState; rejectValue: string }
>(
  "moduleLibrary/fetchModules",
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState().moduleLibrary;
      const { filters, sort } = state;

      const queryParams: ListLibraryModulesParams = {
        page: params?.page ?? 1,
        page_size: 20,
        sort_by: sort.field,
        sort_order: sort.order,
      };

      if (filters.search) queryParams.search = filters.search;
      if (filters.status) queryParams.status = filters.status;
      if (filters.difficulty_level)
        queryParams.difficulty_level = filters.difficulty_level;
      if (filters.tags.length) queryParams.tags = filters.tags;
      if (filters.skill_ids.length) queryParams.skill_ids = filters.skill_ids;

      const response = await fetchLibraryModules(queryParams);
      return {
        modules: response.modules,
        pagination: response.pagination,
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch modules";
      return rejectWithValue(message);
    }
  },
);

/**
 * Fetch a single library module by ID
 */
export const fetchModuleByIdThunk = createAsyncThunk<
  GetLibraryModuleResponse,
  { id: number; includeUsage?: boolean },
  { rejectValue: string }
>(
  "moduleLibrary/fetchModuleById",
  async ({ id, includeUsage = false }, { rejectWithValue }) => {
    try {
      const response = await fetchLibraryModuleById(id, {
        includeContentBlocks: true,
        includeUsage,
      });
      return response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch module";
      return rejectWithValue(message);
    }
  },
);

/**
 * Create a new library module
 */
export const createModuleThunk = createAsyncThunk<
  LibraryModule,
  CreateLibraryModuleRequest,
  { rejectValue: string }
>("moduleLibrary/createModule", async (data, { rejectWithValue }) => {
  try {
    const response = await createLibraryModule(data);
    return response;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Failed to create module";
    return rejectWithValue(message);
  }
});

/**
 * Update an existing library module
 */
export const updateModuleThunk = createAsyncThunk<
  LibraryModule,
  { id: number; data: UpdateLibraryModuleRequest },
  { rejectValue: string }
>("moduleLibrary/updateModule", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateLibraryModule(id, data);
    return response;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Failed to update module";
    return rejectWithValue(message);
  }
});

/**
 * Delete (archive) a library module
 */
export const deleteModuleThunk = createAsyncThunk<
  { id: number; permanent: boolean },
  { id: number; permanent?: boolean },
  { rejectValue: string }
>(
  "moduleLibrary/deleteModule",
  async ({ id, permanent = false }, { rejectWithValue }) => {
    try {
      await deleteLibraryModule(id, permanent);
      return { id, permanent };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete module";
      return rejectWithValue(message);
    }
  },
);

/**
 * Publish a library module
 */
export const publishModuleThunk = createAsyncThunk<
  LibraryModule,
  number,
  { rejectValue: string }
>("moduleLibrary/publishModule", async (id, { rejectWithValue }) => {
  try {
    const response = await publishLibraryModule(id);
    return response.module;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Failed to publish module";
    return rejectWithValue(message);
  }
});

/**
 * Fetch available tags
 */
export const fetchTagsThunk = createAsyncThunk<
  string[],
  void,
  { rejectValue: string }
>("moduleLibrary/fetchTags", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchLibraryTags();
    return response.tags;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Failed to fetch tags";
    return rejectWithValue(message);
  }
});

// ============================================================================
// Slice
// ============================================================================

const moduleLibrarySlice = createSlice({
  name: "moduleLibrary",
  initialState,
  reducers: {
    // Filter actions
    setFilters: (
      state,
      action: PayloadAction<Partial<ModuleLibraryFilters>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialFilters;
    },

    // Sort actions
    setSort: (state, action: PayloadAction<ModuleLibrarySort>) => {
      state.sort = action.payload;
    },

    // Selection actions
    setSelectedModule: (state, action: PayloadAction<LibraryModule | null>) => {
      state.selectedModule = action.payload;
      state.selectedModuleUsage = [];
    },
    setSelectedModuleUsage: (
      state,
      action: PayloadAction<LibraryModuleUsage[]>,
    ) => {
      state.selectedModuleUsage = action.payload;
    },

    // Editor actions
    setEditorMode: (state, action: PayloadAction<"create" | "edit" | null>) => {
      state.editorMode = action.payload;
    },
    setFormData: (
      state,
      action: PayloadAction<ModuleLibraryFormData | null>,
    ) => {
      state.formData = action.payload;
      state.isDirty = false;
    },
    updateFormData: (
      state,
      action: PayloadAction<Partial<ModuleLibraryFormData>>,
    ) => {
      if (state.formData) {
        state.formData = { ...state.formData, ...action.payload };
        state.isDirty = true;
      }
    },
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },

    // Initialize editor for create
    initCreateEditor: (state) => {
      state.editorMode = "create";
      state.selectedModule = null;
      state.formData = {
        title: "",
        description: "",
        difficulty_level: "",
        estimated_minutes: "",
        tags: [],
        skill_ids: [],
        content_blocks: [
          {
            id: crypto.randomUUID(),
            type: "text",
            content:
              "# Module Content\n\nStart writing your module content here...",
            order_number: 0,
          },
        ],
      };
      state.isDirty = false;
    },

    // Initialize editor for edit
    initEditEditor: (state, action: PayloadAction<LibraryModule>) => {
      const module = action.payload;
      state.editorMode = "edit";
      state.selectedModule = module;
      state.formData = {
        title: module.title,
        description: module.description ?? "",
        difficulty_level: module.difficulty_level ?? "",
        estimated_minutes: module.estimated_minutes ?? "",
        tags: module.tags ?? [],
        skill_ids: module.skill_ids ?? [],
        content_blocks:
          module.content_blocks?.map((block, index) => ({
            id: crypto.randomUUID(),
            type: block.type,
            content: block.content,
            order_number: index,
            metadata: block.metadata ?? undefined,
          })) ?? [],
      };
      state.isDirty = false;
    },

    // Reset editor
    resetEditor: (state) => {
      state.editorMode = null;
      state.formData = null;
      state.isDirty = false;
    },

    // Clear errors
    clearErrors: (state) => {
      state.error = {
        list: null,
        detail: null,
        create: null,
        update: null,
        delete: null,
        publish: null,
        tags: null,
      };
    },

    // Reset entire state
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch modules
      .addCase(fetchModulesThunk.pending, (state) => {
        state.loading.list = true;
        state.error.list = null;
      })
      .addCase(fetchModulesThunk.fulfilled, (state, action) => {
        state.loading.list = false;
        state.modules = action.payload.modules;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchModulesThunk.rejected, (state, action) => {
        state.loading.list = false;
        state.error.list = action.payload ?? "Failed to fetch modules";
      })

      // Fetch module by ID
      .addCase(fetchModuleByIdThunk.pending, (state) => {
        state.loading.detail = true;
        state.error.detail = null;
      })
      .addCase(fetchModuleByIdThunk.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.selectedModule = action.payload;
        state.selectedModuleUsage = action.payload.usage ?? [];
      })
      .addCase(fetchModuleByIdThunk.rejected, (state, action) => {
        state.loading.detail = false;
        state.error.detail = action.payload ?? "Failed to fetch module";
      })

      // Create module
      .addCase(createModuleThunk.pending, (state) => {
        state.loading.create = true;
        state.error.create = null;
      })
      .addCase(createModuleThunk.fulfilled, (state, action) => {
        state.loading.create = false;
        state.modules.unshift(action.payload);
        state.selectedModule = action.payload;
        state.editorMode = null;
        state.formData = null;
        state.isDirty = false;
      })
      .addCase(createModuleThunk.rejected, (state, action) => {
        state.loading.create = false;
        state.error.create = action.payload ?? "Failed to create module";
      })

      // Update module
      .addCase(updateModuleThunk.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
      })
      .addCase(updateModuleThunk.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.modules.findIndex(
          (m) => m.id === action.payload.id,
        );
        if (index !== -1) {
          state.modules[index] = action.payload;
        }
        state.selectedModule = action.payload;
        state.isDirty = false;
      })
      .addCase(updateModuleThunk.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload ?? "Failed to update module";
      })

      // Delete module
      .addCase(deleteModuleThunk.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(deleteModuleThunk.fulfilled, (state, action) => {
        state.loading.delete = false;
        if (action.payload.permanent) {
          state.modules = state.modules.filter(
            (m) => m.id !== action.payload.id,
          );
        } else {
          // For archive, update status
          const index = state.modules.findIndex(
            (m) => m.id === action.payload.id,
          );
          if (index !== -1) {
            state.modules[index].status = "archived";
          }
        }
        if (state.selectedModule?.id === action.payload.id) {
          state.selectedModule = null;
        }
      })
      .addCase(deleteModuleThunk.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete = action.payload ?? "Failed to delete module";
      })

      // Publish module
      .addCase(publishModuleThunk.pending, (state) => {
        state.loading.publish = true;
        state.error.publish = null;
      })
      .addCase(publishModuleThunk.fulfilled, (state, action) => {
        state.loading.publish = false;
        const index = state.modules.findIndex(
          (m) => m.id === action.payload.id,
        );
        if (index !== -1) {
          state.modules[index] = action.payload;
        }
        state.selectedModule = action.payload;
      })
      .addCase(publishModuleThunk.rejected, (state, action) => {
        state.loading.publish = false;
        state.error.publish = action.payload ?? "Failed to publish module";
      })

      // Fetch tags
      .addCase(fetchTagsThunk.pending, (state) => {
        state.loading.tags = true;
        state.error.tags = null;
      })
      .addCase(fetchTagsThunk.fulfilled, (state, action) => {
        state.loading.tags = false;
        state.availableTags = action.payload;
      })
      .addCase(fetchTagsThunk.rejected, (state, action) => {
        state.loading.tags = false;
        state.error.tags = action.payload ?? "Failed to fetch tags";
      });
  },
});

// ============================================================================
// Actions
// ============================================================================

export const {
  setFilters,
  resetFilters,
  setSort,
  setSelectedModule,
  setSelectedModuleUsage,
  setEditorMode,
  setFormData,
  updateFormData,
  setIsDirty,
  initCreateEditor,
  initEditEditor,
  resetEditor,
  clearErrors,
  resetState,
} = moduleLibrarySlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectModules = (state: RootState) =>
  state.moduleLibrary?.modules ?? [];
export const selectPagination = (state: RootState) =>
  state.moduleLibrary?.pagination;
export const selectFilters = (state: RootState) =>
  state.moduleLibrary?.filters ?? initialFilters;
export const selectSort = (state: RootState) =>
  state.moduleLibrary?.sort ?? initialSort;
export const selectSelectedModule = (state: RootState) =>
  state.moduleLibrary?.selectedModule;
export const selectSelectedModuleUsage = (state: RootState) =>
  state.moduleLibrary?.selectedModuleUsage ?? [];
export const selectEditorMode = (state: RootState) =>
  state.moduleLibrary?.editorMode;
export const selectFormData = (state: RootState) =>
  state.moduleLibrary?.formData;
export const selectIsDirty = (state: RootState) =>
  state.moduleLibrary?.isDirty ?? false;
export const selectAvailableTags = (state: RootState) =>
  state.moduleLibrary?.availableTags ?? [];
export const selectLoading = (state: RootState) => state.moduleLibrary?.loading;
export const selectErrors = (state: RootState) => state.moduleLibrary?.error;

// Published modules only (for browsing in course editor)
export const selectPublishedModules = (state: RootState) =>
  (state.moduleLibrary?.modules ?? []).filter((m) => m.status === "published");

// ============================================================================
// Reducer
// ============================================================================

export default moduleLibrarySlice.reducer;
